import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import type { SWRInfiniteKeyedMutator } from 'swr/infinite';

import useUserCertificatesFilters from 'containers/CertificatesOverview/UserCertificatesTable/useUserCertificatesFilters';
import { accessMatrixBaseUrl } from 'data/access-control-api-hooks';
import { useApi, usePostSWRFetcher } from 'data/base-api';
import { overviewCertificatesUrl } from 'data/certificates';
import useCurrentUserAndSettings from 'hooks/useCurrentUserAndSettings';
import useHandleError from 'hooks/useHandleError';
import useShowAlert from 'hooks/useShowAlert';
import { checkStatus, del, get, patch, post, put } from 'libs/apiClient';
import { localeCompare } from 'libs/sortFunctions';
import { reduceToObjectById } from 'libs/utils';
import type { ProjectSettings } from 'model/Alerts/types';
import type {
  BatchUpdateUserCertificate,
  UserCertificate,
} from 'model/CertificatesOverview/types';
import type {
  Employment,
  EmploymentValidationResponse,
} from 'model/Company/types';
import type {
  AccessibleUser,
  BestMatchingProfile,
  Certificate,
  ChangesHistory,
  DetailedEmployeeAndSubcontractorUser,
  EmployeeAndSubcontractorUser,
  NewCertificate,
  NewEmployment,
  NewUserData,
  ProjectUserRole,
  SMS,
  Tag,
  User,
  UserExistsCheckByName,
  UserExistsCheckByPhoneNumber,
  UserExistsCheckResponse,
  UserForSearch,
  UserGroup,
  UserV3Api,
} from 'model/User/types';
import type {
  UserProject,
  UserProjectOverview,
} from 'model/UserProjectSettings/types';
import UserCacheService from 'services/user-cache-service';

import { uploadFile } from './file-service';

export const userBaseUrl = '/api/v4/users';
const legacyBaseUrl = '/api/userlegacy';
const appBaseUrl = '/api/user'; // TODO: agree on a final place for tags history api
export const certificatesBaseUrl = '/api/v4/user-certificate';
const baseUrlV3 = '/api/v3/user';

export const useUsersRaw = (
  ignoreUserFiltering = false,
  fetchInactiveUsers = false,
  revalidateOnMount = false
) => {
  const { currentUser } = useCurrentUserAndSettings();
  const handleError = useHandleError();

  const url = `/api/v4/users?ignoreUserFiltering=${ignoreUserFiltering}&activeInactiveAll=${fetchInactiveUsers ? 2 : 0}`;

  // Get cached data as fallback (scoped by company ID)
  const companyId = currentUser?.companyId;
  const cacheKey = React.useMemo(
    () =>
      companyId
        ? UserCacheService.getUsersV4Key(
            companyId,
            fetchInactiveUsers,
            ignoreUserFiltering
          )
        : null,
    [companyId, fetchInactiveUsers, ignoreUserFiltering]
  );
  const cachedData = React.useMemo(
    () =>
      cacheKey
        ? UserCacheService.get<EmployeeAndSubcontractorUser[]>(cacheKey)
        : null,
    [cacheKey]
  );

  // Also check IndexedDB for large datasets (async)
  const [indexedDBData, setIndexedDBData] = React.useState<
    EmployeeAndSubcontractorUser[] | null
  >(null);

  React.useEffect(() => {
    // Only check IndexedDB if localStorage doesn't have data and we have a cache key
    if (!cachedData && cacheKey) {
      UserCacheService.getAsync<EmployeeAndSubcontractorUser[]>(cacheKey)
        .then((data) => {
          if (data) {
            setIndexedDBData(data);
          }
        })
        .catch(() => {
          // Ignore errors silently
        });
    }
  }, [cacheKey, cachedData, url]);

  // Use cached data from localStorage (sync) or IndexedDB (async)
  const fallbackData = cachedData || indexedDBData || undefined;

  const { data, isFetching, ...result } = useApi<
    EmployeeAndSubcontractorUser[]
  >(url, {
    revalidateOnFocus: false,
    revalidateOnMount,
  });

  // Save to cache when fresh data arrives
  // Automatically uses localStorage or IndexedDB based on size
  useEffect(() => {
    if (data && data !== cachedData && data !== indexedDBData && cacheKey) {
      UserCacheService.set(cacheKey, data);
    }
  }, [data, cacheKey, cachedData, indexedDBData]);

  useEffect(() => {
    if (result.error) {
      handleError(result.error);
    }
  }, [result.error, handleError]);

  return {
    // Use cached data as fallback for instant UI feedback
    data: data || fallbackData,
    isFetching: isFetching && !fallbackData,
    ...result,
  };
};

export const useGetUsers = (
  revalidateOnMount = false,
  includeInactive = false
) => {
  const {
    data,
    error,
    mutate: activeMutate,
    ...rest
  } = useUsersRaw(false, includeInactive, revalidateOnMount);

  const activeUsers = useMemo(
    () =>
      data
        ?.sort((a, b) => localeCompare(a.fullName, b.fullName))
        .filter((user) => !user.isDisabled) || [],
    [data]
  );

  const activeUsersById = useMemo(
    () => (activeUsers ? reduceToObjectById(activeUsers) : undefined),
    [activeUsers]
  );

  const usersById = useMemo(
    () =>
      data
        ? reduceToObjectById(
            data.sort((a, b) => localeCompare(a.fullName, b.fullName))
          )
        : undefined,
    [data]
  );

  return {
    users: data,
    activeUsers,
    usersById,
    activeUsersById,
    error,
    ...rest,
  };
};

export const useGetSoonToBeOrActiveUsers = (revalidateOnMount = false) => {
  const { users, isFetching, ...rest } = useGetUsers(revalidateOnMount, true);

  const soonToBeOrActiveUsers = useMemo(
    () =>
      users
        ?.sort((a, b) => localeCompare(a.fullName, b.fullName))
        .filter(
          (user) => !user.isDisabled || user.employment?.isFutureEmployment
        ) || [],
    [users]
  );

  const soonToBeOrActiveUsersById = useMemo(
    () => (users ? reduceToObjectById(soonToBeOrActiveUsers) : undefined),
    [soonToBeOrActiveUsers]
  );

  return {
    users,
    soonToBeOrActiveUsers,
    soonToBeOrActiveUsersById,
    isFetching,
    ...rest,
  };
};

export const useGetActiveUsersSorted = (
  revalidateOnMount = false,
  ignoreUserFiltering = false
) => {
  const { data, error } = useUsersRaw(
    ignoreUserFiltering,
    false,
    revalidateOnMount
  );

  const sortedActiveUsers = useMemo(
    () => data?.sort((a, b) => localeCompare(a.fullName, b.fullName)) || [],
    [data]
  );

  return {
    isFetching: !data && !error,
    sortedActiveUsers,
  };
};

export const getEmployeesAndSubcontractors = async (): Promise<
  EmployeeAndSubcontractorUser[]
> => {
  const response = await get(userBaseUrl);

  return response.json();
};

export const useGetCompanyUser = (
  companyProfileId: string | undefined,
  revalidateOnMount = true
) => {
  const response = useApi<DetailedEmployeeAndSubcontractorUser>(
    companyProfileId ? `${userBaseUrl}/${companyProfileId}` : null,
    {
      revalidateOnMount,
    }
  );

  return response;
};

export const getEmployeesAndSubcontractorsById = async (
  companyProfileId: string
): Promise<UserForSearch> => {
  const response = await get(`${userBaseUrl}/${companyProfileId}`);

  return response.json();
};

export const getSmsLog = async (userId: string): Promise<SMS[]> => {
  const response = await get(`${legacyBaseUrl}/${userId}/smslog`);
  return response.json();
};

export const useGetSmsHistory = (userId: string) => {
  const { data, ...rest } = useApi<SMS[]>(`${legacyBaseUrl}/${userId}/smslog`);

  return {
    smsHistory: data,
    ...rest,
  };
};

export const uploadUserAvatar = async (
  file: File,
  companyProfileId: string | null = null
): Promise<string> => {
  const response = await uploadFile<string>({
    file,
    params: companyProfileId
      ? {
          collectionRef: 'UserCompanyProfiles',
          idRef: companyProfileId,
        }
      : undefined,
    uploadUrl: `${userBaseUrl}/upload-avatar`,
  });

  return response;
};

export const changePhoneNumber = async (
  companyProfileId: string,
  newPhoneNumber: string
): Promise<DetailedEmployeeAndSubcontractorUser> => {
  const response = checkStatus(
    await post(
      `${userBaseUrl}/${companyProfileId}/change-phone-number`,
      newPhoneNumber
    )
  );

  const result = await response.json();

  // Invalidate user cache after phone number change (scoped by company)
  // Note: companyId is not available here, but cache invalidation will happen
  // when user data is refetched with new company context
  UserCacheService.invalidateAll();

  return result;
};

export const updatePersonalInfo = async (
  personalInfo: Omit<NewUserData, 'employment'>
): Promise<DetailedEmployeeAndSubcontractorUser> => {
  const response = checkStatus(
    await put(`${userBaseUrl}/${personalInfo.companyProfileId}`, personalInfo)
  );

  const result = await response.json();

  // Invalidate user cache after update
  UserCacheService.invalidateAll();

  return result;
};

export const useUpdateUserAccessLevel = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    user: DetailedEmployeeAndSubcontractorUser,
    {
      accessGroupId,
      companyUserRoleId,
    }:
      | { accessGroupId: string; companyUserRoleId?: never }
      | { accessGroupId?: never; companyUserRoleId: UserGroup }
  ) => {
    mutate<DetailedEmployeeAndSubcontractorUser>(
      `${userBaseUrl}/${user.companyProfileId}`,
      async (data) => {
        try {
          const response = await checkStatus(
            await put(`${userBaseUrl}/${user.companyProfileId}`, {
              ...user,
              accessGroupId:
                typeof companyUserRoleId === 'undefined' ? accessGroupId : null,
              companyUserRoleId:
                typeof companyUserRoleId === 'undefined'
                  ? user.companyUserRoleId
                  : companyUserRoleId,
            })
          );
          showSuccessMessage({
            message: t('AccessControl.AccessLevelUpdated'),
          });

          mutate(
            (key) =>
              typeof key === 'string' &&
              key.startsWith(accessMatrixBaseUrl) &&
              key.includes(user.id)
          );

          const parsed: DetailedEmployeeAndSubcontractorUser =
            await response.json();

          return parsed;
        } catch (error) {
          handleError(error);

          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: {
          ...user,
          accessGroupId:
            typeof companyUserRoleId === 'undefined' ? accessGroupId : null,
          companyUserRoleId:
            typeof companyUserRoleId === 'undefined'
              ? user.companyUserRoleId
              : companyUserRoleId,
        },
      }
    );
  };
};

export const useUpdateSubcontractorAdmin = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    user: DetailedEmployeeAndSubcontractorUser,
    subContractorAdmin: boolean
  ) => {
    mutate<DetailedEmployeeAndSubcontractorUser>(
      `${userBaseUrl}/${user.companyProfileId}`,
      async (data) => {
        try {
          const response = await checkStatus(
            await put(`${userBaseUrl}/${user.companyProfileId}`, {
              ...user,
              subContractorAdmin,
            })
          );
          showSuccessMessage({
            message: t('AccessControl.AccessLevelUpdated'),
          });

          const parsed: DetailedEmployeeAndSubcontractorUser =
            await response.json();

          return parsed;
        } catch (error) {
          handleError(error);

          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: {
          ...user,
          subContractorAdmin,
        },
      }
    );
  };
};

export const createUser = async (
  userRequest: NewUserData
): Promise<DetailedEmployeeAndSubcontractorUser> => {
  const response = checkStatus(await post(userBaseUrl, userRequest));

  const result = await response.json();

  // Invalidate user cache after create
  UserCacheService.invalidateAll();

  return result;
};

export enum UserSearchRoleFilter {
  None,
  User,
  SuperUser,
  Administrator,
}

export const searchForUser = async (
  searchString: string,
  globalSearch = true,
  onlyEmployees = false,
  accessLevel: UserSearchRoleFilter = UserSearchRoleFilter.None,
  companyId: string | null = null
): Promise<UserForSearch[]> => {
  const response = checkStatus(
    await get(
      `${userBaseUrl}/search/?query=${encodeURIComponent(
        searchString
      )}&globalSearch=${globalSearch}${
        accessLevel !== UserSearchRoleFilter.None
          ? `&accessLevelFilter=${accessLevel}`
          : ''
      }${companyId ? `&companyId=${companyId}` : ''}${
        onlyEmployees ? '&onlyEmployees=true' : ''
      }`
    )
  );

  return response.json();
};

export const singleCompanySearchForUser = async (
  searchString: string
): Promise<UserForSearch[]> => searchForUser(searchString, false);

export const verifyDelete = async (
  companyProfileId: string
): Promise<{
  isDeletable: boolean;
  validationErrors: [];
}> => {
  const response = checkStatus(
    await get(`${userBaseUrl}/${companyProfileId}/verify-delete`)
  );

  return response.json();
};

export const deleteUser = async (
  companyProfileId: string
): Promise<{ success: boolean }> => {
  const response = checkStatus(await del(`${userBaseUrl}/${companyProfileId}`));

  const result = await response.json();

  // Invalidate user cache after delete
  UserCacheService.invalidateAll();

  return result;
};

export const sendWelcomeSms = async (companyProfileId: string) => {
  const response = checkStatus(
    await post(`${legacyBaseUrl}/sendapplink/${companyProfileId}`)
  );

  return response.json();
};

export const getTagsHistory = async (userId: string): Promise<Tag> => {
  const response = await get(
    `${appBaseUrl}/user-tags-tracked-changes?userId=${userId}`
  );
  const parsed = await response.json();

  return parsed;
};

export const useGetTagsHistory = (userId: string) => {
  const { data, ...rest } = useApi<Tag[]>(
    userId ? `${appBaseUrl}/user-tags-tracked-changes?userId=${userId}` : null
  );

  return {
    tagHistory: data,
    ...rest,
  };
};

export const useGetProfileHistory = (userId: string) => {
  const { data, ...rest } = useApi<ChangesHistory[]>(
    userId ? `${appBaseUrl}/${userId}/company-profile-change-log` : null
  );

  return { profileHistory: data, ...rest };
};

export const useGetEmploymentHistory = (userId: string) => {
  const { data, ...rest } = useApi<ChangesHistory[]>(
    userId ? `${appBaseUrl}/${userId}/employment-change-log` : null
  );

  return { employmentHistory: data, ...rest };
};

export const resetOfflineData = async (companyProfileId: string) => {
  const response = await checkStatus(
    await post(`${legacyBaseUrl}/sendappofflineresetpush/${companyProfileId}`)
  );
  return response.json();
};

export const resetAllData = async (companyProfileId: string) => {
  const response = await checkStatus(
    await post(`${legacyBaseUrl}/sendappfullresetpush/${companyProfileId}`)
  );
  return response.json();
};

export const requestDiagnosticData = async (companyProfileId: string) => {
  const response = await checkStatus(
    await post(`${legacyBaseUrl}/send-diagnostics-data/${companyProfileId}`)
  );
  return response.json();
};

export const createSupportUser = async (companyId: string) => {
  const response = await checkStatus(
    await post(`${userBaseUrl}/create-support-user/${companyId}`)
  );

  const result = await response.json();

  // Invalidate user cache after creating support user
  UserCacheService.invalidateAll();

  return result;
};

export const downloadUsersById = async (userIds: string[]): Promise<Blob> => {
  const response = await post(`${userBaseUrl}/download/by-profile-ids`, {
    usersCompanyProfileIds: userIds,
  });
  return response.blob();
};

export const useGetUsersCertificates = (
  userIds: string[],
  revalidateOnMount = true
) => {
  const { data, ...rest } = usePostSWRFetcher<Certificate[], string[]>(
    userIds ? `${certificatesBaseUrl}/fetch-for-users` : null,
    userIds,
    { revalidateOnMount }
  );

  const certificateTypes: string[] = React.useMemo(() => {
    let types = [];
    data?.forEach(({ typeId }) => {
      if (typeId && !types.includes(typeId)) {
        types.push(typeId);
      }
    }, []);
    types = types.sort((a, b) =>
      localeCompare(a.trim().toLocaleLowerCase(), b.trim().toLocaleLowerCase())
    );

    return types;
  }, [data]);

  return {
    certificates: data,
    certificateTypes,
    ...rest,
  };
};

export const useGetAccessibleUsers = () => {
  const { data, ...rest } = useApi<AccessibleUser[]>('api/user/lookuplist/all');

  return {
    accesibleUsers: data,
    ...rest,
  };
};

export const changeCompanyProfile = async (companyProfileId: string) => {
  const response = await checkStatus(
    await put(`${baseUrlV3}/changecompanyprofile/${companyProfileId}`)
  );
  return response.json();
};

export const useGetUserCertificates = (userId: string | null) => {
  const { data, ...rest } = useApi<Certificate[]>(
    userId ? `${certificatesBaseUrl}/${userId}` : null
  );

  return {
    userCertificates: data,
    ...rest,
  };
};

export const useCreateUserCertificate = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    userId: string,
    newCertificate: NewCertificate
  ): Promise<Certificate | null> => {
    try {
      const response = await checkStatus(
        await post(`${certificatesBaseUrl}/${userId}`, newCertificate)
      );
      mutate(overviewCertificatesUrl);
      showSuccessMessage({
        message: t('User.Certificates.AddCertificate_SuccessMessage'),
      });
      const parsed = await response.json();

      return parsed;
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useUpdateUserCertificate = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    userId: string,
    newCertificate: NewCertificate
  ): Promise<Certificate | null> => {
    try {
      const response = await checkStatus(
        await put(`${certificatesBaseUrl}/${userId}`, newCertificate)
      );
      mutate(overviewCertificatesUrl);
      showSuccessMessage({
        message: t('User.Certificates.EditCertificate_SuccessMessage'),
      });
      const parsed = await response.json();

      return parsed;
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useBatchUpdateUserCertificates = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage, showErrorMessage } = useShowAlert();

  return async (
    newCertificates: BatchUpdateUserCertificate[],
    mutateCertificates: SWRInfiniteKeyedMutator<
      {
        more: boolean;
        data: UserCertificate[];
      }[]
    >
  ) => {
    mutateCertificates(
      async (data) => {
        try {
          const response = await checkStatus(
            await patch(`${certificatesBaseUrl}/batch`, newCertificates)
          );

          const parsed: BatchUpdateUserCertificate[] = await response.json();

          if (parsed.length === 0) {
            showErrorMessage({
              message: t(
                'CertificatesOverview.BatchEditingDialog.CertificatesCountUpdated',
                {
                  count: 0,
                }
              ),
            });
          } else {
            showSuccessMessage({
              message: t(
                'CertificatesOverview.BatchEditingDialog.CertificatesCountUpdated',
                {
                  count: parsed.length,
                  submittedCount: newCertificates.length,
                }
              ),
            });
          }

          if (data && parsed) {
            return data.map((entry) => ({
              more: entry.more,
              data: entry.data.map((i) => {
                const parsedItem = parsed.find((p) => p.id === i.id);
                if (parsedItem) {
                  return {
                    ...i,
                    ...parsedItem,
                  };
                }

                return i;
              }),
            }));
          }

          return data;
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) => {
          if (!data) {
            return [
              {
                more: false,
                data: [],
              },
            ];
          }

          return data.map((entry) => ({
            more: entry.more,
            data: entry.data.map((i) => {
              const parsedItem = newCertificates.find((p) => p.id === i.id);
              if (parsedItem) {
                return {
                  ...i,
                  ...parsedItem,
                };
              }

              return i;
            }),
          }));
        },
      }
    );
  };
};

export const useGetAvailableProjects = (skip = false) =>
  useApi<UserProject[]>(skip ? null : `${userBaseUrl}/available-projects`);

export const useGetProjectSettingsForUsers = (projectId: string | null) =>
  useApi<ProjectSettings>(
    projectId && `${userBaseUrl}/project-settings/${projectId}`
  );

export const useGetUserProjectsOverview = (
  userId: string | null,
  includeInactive = false
) =>
  useApi<UserProjectOverview[]>(
    userId &&
      `${userBaseUrl}/project-user-setting/${userId}?includeInactive=${includeInactive}`
  );

export const useGetUserProjectSetup = (
  userId: string | null,
  projectId: string | null
) =>
  useApi<ProjectSettings>(
    userId &&
      projectId &&
      `${userBaseUrl}/project-user-setting/${userId}/${projectId}?resourceFilter=2`
  );

export const searchForUsers = async (
  searchString: string
): Promise<UserV3Api[]> => {
  if (!searchString.length) {
    return [];
  }

  const response = checkStatus(
    await get(
      `${appBaseUrl}/usersearch?searchString=${searchString}&activeOnly=true`
    )
  );

  return response.json();
};

export const useGetUserById = (id: string | null | undefined) =>
  useApi<User>(id ? `${baseUrlV3}/${id}` : null);

export const useCheckIfPhoneNumberExists = () => {
  const handleError = useHandleError();

  return async (
    request: UserExistsCheckByPhoneNumber | UserExistsCheckByName
  ) => {
    try {
      const response = checkStatus(
        await post(`${userBaseUrl}/phone-number-exists`, request)
      );
      const parsed: UserExistsCheckResponse | null = await response.json();

      return parsed;
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useValidateEmployments = () => {
  const handleError = useHandleError();

  return async (
    userId: string,
    employments: { startDate: string; endDate: string }[]
  ) => {
    try {
      const response = checkStatus(
        await post(`${userBaseUrl}/employment-validation`, {
          userId,
          employments,
        })
      );
      const parsed: EmploymentValidationResponse = await response.json();

      return parsed;
    } catch (e) {
      handleError(e);
      return e;
    }
  };
};

export const useSaveEmployments = () => {
  const handleError = useHandleError();

  return async (
    employments: NewEmployment[],
    companyProfileId: string,
    callBack: (message: string, errorData?: any) => void
  ) => {
    try {
      const response = checkStatus(
        await put(`${userBaseUrl}/employments`, employments)
      );
      mutate(`${userBaseUrl}/${companyProfileId}`);
      const parsed: Employment[] = await response.json();
      return parsed;
    } catch (e) {
      handleError(e, undefined, ({ message, additionalData }) =>
        callBack(message, additionalData)
      );
      return e;
    }
  };
};

export const useGetBestMatchingUserProfile = (
  userId: string | null,
  revalidateOnMount = true
) =>
  useApi<BestMatchingProfile>(
    userId ? `${userBaseUrl}/best-matching-user-profile/${userId}` : null,
    {
      revalidateOnMount,
    }
  );

export const useGetBestMatchingUserProfileOnce = () => {
  const handleError = useHandleError();

  return async (userId: string) => {
    try {
      const response = checkStatus(
        await get(`${userBaseUrl}/best-matching-user-profile/${userId}`)
      );

      const parsed: BestMatchingProfile = await response.json();
      return parsed;
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useSendReminder = (typeId?: string) => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  const { search, onlyExpired, tagIds } = useUserCertificatesFilters();

  return async (
    certificates: UserCertificate[] | Certificate,
    message: string
  ) => {
    try {
      const userIds = Array.isArray(certificates)
        ? Array.from(new Set(certificates.map((c) => c.user.id)))
        : [certificates.userId];

      checkStatus(
        await post(`${certificatesBaseUrl}/send-reminder`, {
          certificateIds: Array.isArray(certificates)
            ? certificates.map((c) => c.id)
            : [certificates.id],
          message,
          filters: {
            typeId,
            search,
            onlyExpiredOrNotificationDateDue: onlyExpired,
            tagIds,
          },
        })
      );

      showSuccessMessage({
        message:
          (Array.isArray(certificates) && certificates.length > 0) ||
          !Array.isArray(certificates)
            ? t('CertificatesOverview.Reminder.SentToUsers', {
                count: userIds.length,
              })
            : t('CertificatesOverview.Reminder.SentToAllUsers'),
      });
    } catch (e) {
      handleError(e);
    }
  };
};

export const useMaintainUserProjectRoles = () => {
  const handleError = useHandleError();

  return async (
    userId: string,
    projectUserRole: ProjectUserRole,
    projectIds: string[]
  ) => {
    try {
      await checkStatus(
        await post(
          `${userBaseUrl}/project-user-setting/${userId}/maintain-roles`,
          {
            projectIds,
            projectUserRole,
          }
        )
      );

      mutate(
        (key) =>
          typeof key === 'string' &&
          (key.startsWith(`${userBaseUrl}/project-user-setting/${userId}`) ||
            (key.startsWith(`${accessMatrixBaseUrl}`) && key.includes(userId)))
      );
    } catch (e) {
      handleError(e);
    }
  };
};
